import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type PropsWithChildren,
  type ReactElement,
  type RefObject,
} from 'react'
import { Interactive } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'

const OVERLAY_MARGIN = 8

interface OverlayPosition {
  x: number
  y: number
}

interface DraggablePanel {
  panelRef: RefObject<HTMLDivElement>
  position: OverlayPosition | null
  dragging: boolean
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void
  onPointerEnd: (event: ReactPointerEvent<HTMLDivElement>) => void
}

interface DebugOverlayProps extends PropsWithChildren {
  label: string
  onClose: () => void
  placementClassName?: string
  className?: string
  selectable?: boolean
}

function useDraggablePanel(enabled: boolean): DraggablePanel {
  const [position, setPosition] = useState<OverlayPosition | null>(null)
  const [dragging, setDragging] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null)

  useEffect(() => {
    if (enabled) {
      return
    }
    setPosition(null)
    setDragging(false)
    dragRef.current = null
  }, [enabled])

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0 || !panelRef.current) {
      return
    }
    event.preventDefault()
    const bounds = panelRef.current.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging(true)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current
    const panel = panelRef.current
    if (!drag || drag.pointerId !== event.pointerId || !panel) {
      return
    }
    const maxX = Math.max(OVERLAY_MARGIN, window.innerWidth - panel.offsetWidth - OVERLAY_MARGIN)
    const maxY = Math.max(OVERLAY_MARGIN, window.innerHeight - panel.offsetHeight - OVERLAY_MARGIN)
    setPosition({
      x: Math.max(OVERLAY_MARGIN, Math.min(event.clientX - drag.offsetX, maxX)),
      y: Math.max(OVERLAY_MARGIN, Math.min(event.clientY - drag.offsetY, maxY)),
    })
  }

  const onPointerEnd = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (dragRef.current?.pointerId !== event.pointerId) {
      return
    }
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    setDragging(false)
  }

  return { panelRef, position, dragging, onPointerDown, onPointerMove, onPointerEnd }
}

export function DebugOverlay({
  label,
  onClose,
  placementClassName = 'bottom-2 left-2',
  className,
  selectable = false,
  children,
}: DebugOverlayProps): ReactElement {
  const [minimized, setMinimized] = useState(false)
  const { panelRef, position, dragging, onPointerDown, onPointerMove, onPointerEnd } = useDraggablePanel(!minimized)

  if (minimized) {
    return (
      <div
        className={twMerge(
          'text-foreground pointer-events-auto fixed z-[9999] select-none rounded-lg bg-white/95 font-mono text-[10px] leading-tight dark:bg-black/95',
          placementClassName,
        )}
      >
        <Interactive>
          <button
            type="button"
            aria-label={`Expand ${label}`}
            className="hover:bg-element-hover flex h-7 cursor-pointer items-center gap-2 rounded-lg bg-transparent px-2.5"
            onClick={() => setMinimized(false)}
          >
            <span className="text-foreground-secondary">{label}</span>
            <span className="text-foreground-placeholder text-sm leading-none">+</span>
          </button>
        </Interactive>
      </div>
    )
  }

  return (
    <div
      ref={panelRef}
      className={twMerge(
        'text-foreground pointer-events-auto fixed z-[9999] flex flex-col rounded-lg bg-white/95 font-mono text-[10px] leading-tight dark:bg-black/95',
        selectable ? 'select-text' : 'select-none',
        placementClassName,
        className,
      )}
      style={position ? { bottom: 'auto', left: position.x, top: position.y } : undefined}
    >
      <Interactive>
        <div
          className={twMerge(
            'text-foreground-disabled relative flex h-5 shrink-0 cursor-grab touch-none items-center justify-center p-2.5 pb-0',
            dragging && 'cursor-grabbing',
          )}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        >
          <span className="h-0.5 w-8 rounded-full bg-current" />
          <button
            type="button"
            aria-label={`Minimize ${label}`}
            className="text-foreground-placeholder hover:bg-element-hover hover:text-foreground absolute right-5 top-0.5 flex size-4 cursor-pointer items-center justify-center rounded bg-transparent text-sm leading-none"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => setMinimized(true)}
          >
            −
          </button>
          <button
            type="button"
            aria-label={`Close ${label}`}
            className="text-foreground-placeholder hover:bg-element-hover hover:text-foreground absolute right-1 top-0.5 flex size-4 cursor-pointer items-center justify-center rounded bg-transparent text-sm leading-none"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {children}
      </Interactive>
    </div>
  )
}
